import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { useClientLogin } from "../hooks/useClientAuth";
import { useClientAuth } from "../ClientAuthContext";
import { useBookingDraft } from "../../booking/BookingDraftContext";
import { useTranslation } from "react-i18next";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { phoneCodeExample } from "@/lib/validation/contact";

interface Props {
    onClose: () => void;
    onSwitchToSignup: () => void;
}

// Validation helper
function isValidPhone(phone: string): boolean {
    if (!phone.startsWith("+")) return false;
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length >= 5;
}

export default function ClientLoginView({ onClose, onSwitchToSignup }: Props) {
    const { t } = useTranslation();
    const [phone, setPhone] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);
    const [phoneTouched, setPhoneTouched] = React.useState(false);

    // Same shared key/default as the confirm + waitlist screens so the
    // translator sees one consistent default; {{code}} = salon phone_code.
    const phoneError = phoneTouched && !isValidPhone(phone)
        ? t("widget.common.validation.phone_country_code_hint", "Phone number must start with the country code (for example, {{code}})", { code: phoneCodeExample(getCompanyRegistry().getSettings()?.phone_code) })
        : null;

    const loginMutation = useClientLogin();
    const { actions } = useClientAuth();
    const { draft } = useBookingDraft();
    const companyId = draft.companyId ?? 0;

    const handleLogin = async () => {
        setError(null);
        try {
            const res = await loginMutation.mutateAsync({
                phone,
                password,
                company_id: companyId,
            });
            actions.login({
                accessToken: res.access_token,
                clientId: res.client_id,
                phone: res.phone,
                expiresAt: Date.now() + res.expires_in * 1000,
                isVerified: res.is_verified,
                name: res.name,
            });
        } catch (e: any) {
            const msg = e?.response?.data?.detail || e?.message || t("widget.client.login.error", "Login error");
            setError(msg);
        }
    };

    return (
        <div className="bg-background h-[100dvh] min-h-[100dvh]">
            <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
                <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-border/10 flex items-center justify-between">
                        <h2 className="text-lg font-semibold">{t("widget.client.login.title", "Log in to your account")}</h2>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {/* Platform migration notice */}
                        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 mb-4">
                            {t("widget.client.platform_notice", "We now use the new Boona online booking platform. You need to register again.")}
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-2 mb-4">
                            <Button variant="default" className="flex-1 rounded-full">{t("widget.client.login.tab", "Log in")}</Button>
                            <Button variant="outline" className="flex-1 rounded-full" onClick={onSwitchToSignup}>
                                {t("widget.client.signup.tab", "Sign up")}
                            </Button>
                        </div>

                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">{t("widget.common.phone", "Phone")}</Label>
                                <Input
                                    type="tel"
                                    placeholder="+375 (__) ___-__-__"
                                    value={phone}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                                    onBlur={() => setPhoneTouched(true)}
                                    className={phoneError ? "border-red-500" : ""}
                                />
                                {phoneError && <p className="text-xs text-red-500">{phoneError}</p>}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">{t("widget.common.password", "Password")}</Label>
                                <Input
                                    type="password"
                                    placeholder={t("widget.client.login.password_placeholder", "Enter your password")}
                                    value={password}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                />
                            </div>

                            {error && (
                                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer CTA */}
                    <div
                        className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur"
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
                    >
                        <div className="p-4 pt-3">
                            <Button
                                className="w-full h-12 rounded-xl font-semibold"
                                onClick={handleLogin}
                                disabled={loginMutation.isPending || !isValidPhone(phone) || !password}
                            >
                                {loginMutation.isPending ? t("widget.client.login.submitting", "Logging in...") : t("widget.client.login.submit", "Log in")}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
